import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Button,
  IconButton,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchCompanyDevices,
  unlinkDeviceFromCompany,
  resetForCompany,
  sendSoftwareUpdate,
} from "../../companyDevices/companyDevicesSlice";
import DeviceForm from "../../companyDevices/DeviceForm";
import DeviceTable from "../../companyDevices/DeviceTable";
import AssignDeviceDialog from "../../companyDevices/AssignDeviceDialog";
import SwapImeiDialog from "../../companyDevices/SwapImeiDialog";
import StatusPanel from "../../companyDevices/StatusPanel";
import ConfigPanel from "../../deviceConfig/ConfigPanel";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../../components/common/DangerConfirmField";

export default function CompanyDevicesTab() {
  const { company } = useOutletContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, loading, companyId, sendingUpdateImei } = useSelector(
    (s) => s.companyDevices
  );
  const [activeStatusImei, setActiveStatusImei] = useState(null);
  const [activeConfigImei, setActiveConfigImei] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [unlinkConfirm, setUnlinkConfirm] = useState("");
  const [updateTarget, setUpdateTarget] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);

  // No "new" form path here anymore — devices are created on /devices and
  // joined to a company via the assign flow there. Only ?edit=:id opens the
  // form, and only against devices already in this company.
  const editId = searchParams.get("edit");
  const editingDevice = useMemo(
    () => (editId ? items.find((d) => d._id === editId) : null),
    [editId, items]
  );
  const formOpen = Boolean(editingDevice);

  useEffect(() => {
    dispatch(resetForCompany(company._id));
  }, [company._id, dispatch]);

  useEffect(() => {
    if (companyId === company._id) {
      dispatch(fetchCompanyDevices({ companyId: company._id }));
    }
  }, [dispatch, company._id, companyId]);

  const onConfirmUpdate = async () => {
    if (!updateTarget) return;
    const res = await dispatch(sendSoftwareUpdate(updateTarget.IMEI));
    if (sendSoftwareUpdate.fulfilled.match(res)) {
      toast.success(`Update sent to ${updateTarget.IMEI}`);
      setUpdateTarget(null);
    } else {
      toast.error(res.payload || "Update failed");
    }
  };

  const openEdit = (device) => {
    setSearchParams({ edit: device._id });
  };

  const closeForm = () => {
    setSearchParams({});
  };

  const onConfirmUnlink = async () => {
    if (!unlinkTarget) return;
    if (!isDangerCodeValid(unlinkConfirm)) return;
    setUnlinking(true);
    const res = await dispatch(unlinkDeviceFromCompany(unlinkTarget._id));
    setUnlinking(false);
    if (unlinkDeviceFromCompany.fulfilled.match(res)) {
      toast.success(
        `Removed ${unlinkTarget.device_name || unlinkTarget.IMEI} from this company`
      );
      if (activeStatusImei === unlinkTarget.IMEI) setActiveStatusImei(null);
      setUnlinkTarget(null);
      setUnlinkConfirm("");
    } else {
      toast.error(res.payload || "Unlink failed");
    }
  };

  const closeUnlink = () => {
    if (unlinking) return;
    setUnlinkTarget(null);
    setUnlinkConfirm("");
  };

  const toggleStatus = (device) => {
    setActiveStatusImei((prev) =>
      prev === device.IMEI ? null : device.IMEI
    );
  };

  const toggleConfig = (device) => {
    setActiveConfigImei((prev) =>
      prev === device.IMEI ? null : device.IMEI
    );
  };

  return (
    <>
      {formOpen && (
        <DeviceForm
          companyId={company._id}
          editingDevice={editingDevice}
          onClose={closeForm}
        />
      )}

      {assignOpen && (
        <AssignDeviceDialog
          company={company}
          onClose={() => setAssignOpen(false)}
        />
      )}

      {swapTarget && (
        <SwapImeiDialog
          device={swapTarget}
          refreshScope={{ kind: "company", companyId: company._id }}
          onClose={() => setSwapTarget(null)}
        />
      )}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {/* Action bar — only an Assign button. The new-device flow lives on
            the global /devices page; here you can only LINK an existing
            one. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            p: 1,
            alignItems: "center",
            justifyContent: "flex-end",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {isMobile ? (
            <IconButton
              color="primary"
              onClick={() => setAssignOpen(true)}
              sx={{
                flexShrink: 0,
                backgroundColor: "primary.main",
                color: "common.white",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => setAssignOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              Assign Device
            </Button>
          )}
        </Stack>

        <Divider />

        <DeviceTable
          items={items}
          loading={loading}
          activeStatusImei={activeStatusImei}
          activeConfigImei={activeConfigImei}
          onToggleStatus={toggleStatus}
          onToggleConfig={toggleConfig}
          onEdit={openEdit}
          onUnlink={setUnlinkTarget}
          onSwap={setSwapTarget}
          onLogs={(d) => navigate(`/devices/${encodeURIComponent(d.IMEI)}/logs`)}
          onUpdate={setUpdateTarget}
          scrollKey={`company-${company._id}-devices`}
          sendingUpdateImei={sendingUpdateImei}
        />
      </Paper>

      {activeStatusImei && (() => {
        const activeDevice = items.find((x) => x.IMEI === activeStatusImei);
        if (!activeDevice) return null;
        return (
          <Paper variant="outlined" sx={{ mt: 1.5 }}>
            <StatusPanel
              device={activeDevice}
              onClose={() => setActiveStatusImei(null)}
            />
          </Paper>
        );
      })()}

      {activeConfigImei && (
        <ConfigPanel
          imei={activeConfigImei}
          onClose={() => setActiveConfigImei(null)}
        />
      )}

      <Dialog
        open={Boolean(unlinkTarget)}
        onClose={closeUnlink}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: { xs: 0, sm: 380 } } }}
        fullWidth
      >
        <DialogTitle sx={{ p: 1.75, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "warning.main" }}>
            Remove Device from Company
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Remove <b>{unlinkTarget?.device_name || unlinkTarget?.IMEI}</b>{" "}
            from <b>{company?.name}</b>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The device record, Powermeter row, and IMEI configuration (c1–c40)
            are <b>kept</b>. Only the company link, station, and admin
            assignments are cleared. The device will appear under the global
            Devices section as unassigned, and can be linked to another company
            later.
          </Typography>
          <DangerConfirmField
            value={unlinkConfirm}
            onChange={setUnlinkConfirm}
            disabled={unlinking}
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={closeUnlink} variant="outlined" disabled={unlinking}>
            Cancel
          </Button>
          <Button
            onClick={onConfirmUnlink}
            variant="contained"
            color="warning"
            disabled={unlinking || !isDangerCodeValid(unlinkConfirm)}
            startIcon={unlinking ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {unlinking ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(updateTarget)}
        onClose={() => sendingUpdateImei ? undefined : setUpdateTarget(null)}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: { xs: 0, sm: 380 } } }}
        fullWidth
      >
        <DialogTitle sx={{ p: 1.75, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Send Software Update
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2">
            Send the powermeter software-update command to{" "}
            <b>{updateTarget?.device_name || updateTarget?.IMEI}</b>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            onClick={() => setUpdateTarget(null)}
            variant="outlined"
            disabled={Boolean(sendingUpdateImei)}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirmUpdate}
            variant="contained"
            disabled={Boolean(sendingUpdateImei)}
            startIcon={
              sendingUpdateImei ? (
                <CircularProgress size={14} color="inherit" />
              ) : null
            }
          >
            {sendingUpdateImei ? "Sending…" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
